import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardContent } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Package, TrendingUp } from "lucide-react";
import BlueprintCard, { FeaturedBlueprintCard } from "./BlueprintCard";

function LoadingSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                    <div className="aspect-[16/10] bg-gray-100 rounded-t-lg" />
                    <CardHeader><div className="h-4 bg-gray-200 rounded w-3/4" /></CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <div className="h-3 bg-gray-200 rounded" />
                            <div className="h-3 bg-gray-200 rounded w-2/3" />
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function EmptyState({ hasActiveFilters, onClearFilters }) {
    const { t } = useTranslation();
    return (
        <div className="text-center py-16 border border-dashed rounded-lg">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{t("blueprints.noBlueprints")}</h3>
            <p className="text-muted-foreground mb-4">
                {hasActiveFilters ? t("blueprints.noSearchResults") : t("blueprints.noBlueprintsAvailable")}
            </p>
            {hasActiveFilters ? (
                <Button variant="outline" onClick={onClearFilters}>{t("blueprints.clearFilters")}</Button>
            ) : null}
        </div>
    );
}

export default function BlueprintGrid({
    blueprints,
    loading,
    hasActiveFilters,
    showFeatured,
    onCreateWorkflow,
    onClearFilters,
}) {
    const { t } = useTranslation();

    if (loading) return <LoadingSkeleton />;
    if (blueprints.length === 0) {
        return <EmptyState hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />;
    }

    // Split featured (top by downloads) from rest when on "All" tab, page 1
    if (showFeatured && blueprints.length >= 4) {
        const sorted = [...blueprints].sort((a, b) => (b.downloadCount || 0) - (a.downloadCount || 0));
        const featured = sorted.slice(0, 3);
        const featuredIds = new Set(featured.map((b) => b.id));
        const rest = blueprints.filter((b) => !featuredIds.has(b.id));

        return (
            <div className="space-y-8">
                {/* Featured section */}
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-5 w-5 text-amber-500" />
                        <h2 className="text-lg font-semibold">{t("blueprints.popularTemplates", "Popular Templates")}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {featured.map((bp) => (
                            <FeaturedBlueprintCard key={bp.id} blueprint={bp} onCreateWorkflow={onCreateWorkflow} />
                        ))}
                    </div>
                </div>

                {/* Rest */}
                {rest.length > 0 ? (
                    <div>
                        <h2 className="text-lg font-semibold mb-4">{t("blueprints.allTemplates", "All Templates")}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {rest.map((bp) => (
                                <BlueprintCard key={bp.id} blueprint={bp} onCreateWorkflow={onCreateWorkflow} />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    // Regular grid (filtered or later pages)
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blueprints.map((bp) => (
                <BlueprintCard key={bp.id} blueprint={bp} onCreateWorkflow={onCreateWorkflow} />
            ))}
        </div>
    );
}
